<script setup lang="ts">
import type { PortableTextMarkComponentProps } from '@portabletext/vue';
import { logger } from '@pkg/logger';
import { ref } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<
  PortableTextMarkComponentProps<{
    // Keys come in under props.value
    _key: string;
    _type: string;
    exposedFunctionId: string;
    functionArguments?: string[];
    ctaStyle?: boolean;
  }>
>();

const isClicked = ref(false);

const handleClick = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
  
  // Add clicked class
  isClicked.value = true;
  
  if (props.value?.exposedFunctionId) {
    try {
      // Call the exposed function with arguments
      const args = props.value.functionArguments || [];
      if (typeof window !== 'undefined' && (window as any).exposedFunctions) {
        (window as any).exposedFunctions.execute(props.value.exposedFunctionId, ...args);
      } else {
        console.error('Exposed functions not available');
      }
    } catch (error) {
      console.error('JavaScript execution error:', error);
      logger.info(`JavaScript execution error: ${(error as Error).message}`);
    }
  }
};
</script>

<template>
  <span
    v-if="props.value?.exposedFunctionId"
    @click="handleClick"
    :class="[
      'c-javascript-link',
      'cursor-pointer underline',
      { 'clicked': isClicked }
    ]"
    :title="`JavaScript: ${props.value?.exposedFunctionId || ''}`"
  >
    <slot />
  </span>
  <span v-else>
    <slot />
  </span>
</template>

<style scoped>
.c-javascript-link {
  color: var(--btn-text-type);
  text-decoration: underline;
  transition: color 0.3s ease;
  position: relative;
}

.c-javascript-link:hover {
  color: var(--btn-text-hover-type);
}

.c-javascript-link[title*="copy-to-clipboard"] {
  position: relative;
  padding-right: 1rem;
  margin-right: 0.25rem;
}

.c-javascript-link[title*="copy-to-clipboard"].clicked:after {
  /* Your clicked styles here */
  content: '✓';
  line-height: 0.875rem;
  font-size: 12px;
}

.c-javascript-link[title*="copy-to-clipboard"]::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0.125rem;
  
  width: 0.75rem;
  height: 0.875rem;
  
  border: 1px solid currentColor;
  border-radius: 0.125rem;
}

.c-javascript-link[title*="copy-to-clipboard"]::before {
  content: '';
  position: absolute;
  right: 0.125rem;
  top: -0.125rem;

  /* Dimensions */
  width: 0.25rem;
  height: 0.125rem;
  
  border: 1px solid currentColor;
  border-top-left-radius: 0.125rem;
  border-top-right-radius: 0.125rem;
  border-bottom: none;
}
</style>
